import { useCallback, useEffect, useRef, useState } from "react";
import {
  useMonoPitchDetector,
  type MonoDetectedNote,
} from "./useMonoPitchDetector";
import type { NoteName } from "../lib/guitarEngine";

// ─────────────────────────────────────────────────────────────
// usePitchDetector — wrapper de compatibilidad sobre
// useMonoPitchDetector (motor YIN unificado).
//
// Mantiene la API original que usan ScaleExplorer, ModoExplorer
// y TunerOverlay para no requerir cambios en esas páginas.
//
// Diferencias respecto a la versión original:
//   – Misma calidad de detección (mismo algoritmo y mejor pipeline).
//   – El mic se mantiene abierto entre startListening / stopListening
//     individuales, lo que reduce hiccups al alternar modos.
//   – startListening es idempotente: llamarla dos veces no duplica el
//     stream del micrófono.
// ─────────────────────────────────────────────────────────────

export interface PitchDetectorState {
  isListening: boolean;
  frequency: number;
  note: NoteName | null;
  octave: number;
  cents: number;
  volume: number;
  confidence: number;
  holdProgress: number;
}

export interface UsePitchDetectorOptions {
  threshold?: number;
  holdTimeMs?: number;
  onNoteDetected?: (note: NoteName, octave: number, frequency: number) => void;
  onNoteStable?: (
    note: NoteName,
    octave: number,
    frequency: number,
    durationMs: number,
  ) => void;
}

export function usePitchDetector(options: UsePitchDetectorOptions = {}) {
  const { threshold = 0.003, holdTimeMs = 0, onNoteDetected, onNoteStable } =
    options;

  // Refs estables para los callbacks (se actualizan en cada render).
  const onNoteDetectedRef = useRef(onNoteDetected);
  const onNoteStableRef = useRef(onNoteStable);
  useEffect(() => {
    onNoteDetectedRef.current = onNoteDetected;
    onNoteStableRef.current = onNoteStable;
  }, [onNoteDetected, onNoteStable]);

  const {
    state: monoState,
    start: startMono,
    stop: stopMono,
    subscribe,
    getLiveVolume,
    getLivePitch,
  } = useMonoPitchDetector();

  // El estado expuesto debe mantener la forma original. Lo derivamos del
  // estado del motor mono más un flag local para isListening
  // (semánticamente: el componente ha pedido escuchar).
  const [activeListen, setActiveListen] = useState(false);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  const startListening = useCallback(async () => {
    try {
      await startMono();
      setActiveListen(true);

      // Cancelar suscripción previa (por si startListening se llama dos
      // veces seguidas).
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      // Suscripción al streaming del motor.
      unsubscribeRef.current = subscribe({
        threshold,
        holdTimeMs,
        onStable: (n: MonoDetectedNote, durationMs: number) => {
          onNoteDetectedRef.current?.(n.note, n.octave, n.frequency);
          onNoteStableRef.current?.(n.note, n.octave, n.frequency, durationMs);
        },
      });
    } catch (err) {
      console.error("[PitchDetector compat] mic error:", err);
      setActiveListen(false);
    }
  }, [startMono, subscribe, threshold, holdTimeMs]);

  const stopListening = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    setActiveListen(false);
    // No detenemos el mic global (otros usuarios podrían estar
    // suscritos). El cleanup del propio useMonoPitchDetector lo cerrará
    // al desmontar este componente.
    stopMono();
  }, [stopMono]);

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, []);

  const state: PitchDetectorState = {
    isListening: activeListen,
    frequency: activeListen ? monoState.frequency : 0,
    note: activeListen ? monoState.note : null,
    octave: activeListen ? monoState.octave : 0,
    cents: activeListen ? monoState.cents : 0,
    volume: activeListen ? monoState.volume : 0,
    confidence: activeListen ? monoState.confidence : 0,
    holdProgress: activeListen ? monoState.holdProgress : 0,
  };

  return { state, startListening, stopListening, getLiveVolume, getLivePitch };
}
